import {
  WORKFLOW_RUNS_WAITING_URL,
  workflowRunApproveUrl,
  workflowRunRejectUrl,
  workflowRunsUrl,
  workflowRunUrl,
  WORKFLOWS_URL,
  workflowToggleUrl,
  workflowTriggerUrl,
  workflowUrl,
} from "../constants/automation-api-urls";
import type {
  ICreateWorkflowPayload,
  IGetWorkflowRunsProps,
  IUpdateWorkflowProps,
  IWorkflow,
  IWorkflowRun,
  IWorkflowRunDetail,
} from "../types/workflow";
import type { ApiClient } from "./client";

// NOTE: NSAI-6936 — the Workflows tab's client for the internal (session-auth)
// WorkflowController routes. Web and mobile both go through this class, so the
// two tabs cannot drift on a URL or a payload shape.
export class WorkflowApi {
  constructor(private apiClient: ApiClient) {}

  async getWorkflows(): Promise<IWorkflow[]> {
    const response = await this.apiClient.get<IWorkflow[]>(WORKFLOWS_URL);
    return response.data;
  }

  async getWorkflow(uuid: string): Promise<IWorkflow> {
    const response = await this.apiClient.get<IWorkflow>(workflowUrl(uuid));
    return response.data;
  }

  async createWorkflow(data: ICreateWorkflowPayload): Promise<IWorkflow> {
    const response = await this.apiClient.post<IWorkflow>(WORKFLOWS_URL, data);
    return response.data;
  }

  async updateWorkflow({
    uuid,
    data,
  }: IUpdateWorkflowProps): Promise<IWorkflow> {
    const response = await this.apiClient.put<IWorkflow>(
      workflowUrl(uuid),
      data,
    );
    return response.data;
  }

  async deleteWorkflow(uuid: string): Promise<void> {
    await this.apiClient.delete(workflowUrl(uuid));
  }

  async toggleWorkflow(uuid: string): Promise<IWorkflow> {
    const response = await this.apiClient.put<IWorkflow>(
      workflowToggleUrl(uuid),
    );
    return response.data;
  }

  async triggerWorkflow(uuid: string): Promise<void> {
    await this.apiClient.post(workflowTriggerUrl(uuid));
  }

  async getWorkflowRuns({
    uuid,
    limit,
  }: IGetWorkflowRunsProps): Promise<IWorkflowRun[]> {
    const response = await this.apiClient.get<IWorkflowRun[]>(
      workflowRunsUrl({ uuid, limit }),
    );
    return response.data;
  }

  async getWaitingWorkflowRuns(): Promise<IWorkflowRun[]> {
    const response = await this.apiClient.get<IWorkflowRun[]>(
      WORKFLOW_RUNS_WAITING_URL,
    );
    return response.data;
  }

  async getWorkflowRunDetail(runUuid: string): Promise<IWorkflowRunDetail> {
    const response = await this.apiClient.get<IWorkflowRunDetail>(
      workflowRunUrl(runUuid),
    );
    return response.data;
  }

  async approveWorkflowRun(runUuid: string): Promise<void> {
    await this.apiClient.post(workflowRunApproveUrl(runUuid));
  }

  async rejectWorkflowRun(runUuid: string): Promise<void> {
    await this.apiClient.post(workflowRunRejectUrl(runUuid));
  }
}
